import { onAuthChange } from '$lib/platform/auth-provider';
import { onOnlineChange } from '$lib/platform/detector';
import { getDocument } from '$lib/firebase/firestore';
import { User } from '$lib/types/User';
import { BaseApiService, AdminApiService, SubscriptionApiService } from '$lib/services/ApiService';
import { AccountService } from '$lib/services/AccountService';
import { signInWithCustomToken } from '$lib/firebase/auth';
import { setLocale } from '$lib/i18n';

const SUPPORTED_LOCALES = ['nl', 'en', 'de'];

function detectBrowserLocale(): string {
  if (typeof navigator === 'undefined') return 'nl';
  const lang = navigator.language?.split('-')[0];
  return SUPPORTED_LOCALES.includes(lang) ? lang : 'nl';
}

export enum AuthState {
  LOADING = 'LOADING',
  AUTHENTICATED = 'AUTHENTICATED',
  UNAUTHENTICATED = 'UNAUTHENTICATED',
  ERROR = 'ERROR'
}

let _state = $state<AuthState>(AuthState.LOADING);
let _user = $state<User | null>(null);
let _adminUser = $state<User | null>(null);
let _error = $state<string | null>(null);
let _isInitialized = $state(false);
let _isOnline = $state(true);

export async function validateOrganizationSeat(user: User) {
  if (!user || !user.subscriptions) return;
  const organizationSubs = user.subscriptions.filter(
    (sub) => sub.type === 'ORGANIZATION' && !sub.isExpired()
  );
  if (organizationSubs.length === 0) return;

  for (const sub of organizationSubs) {
    const orgId = sub.metadata?.orgId;
    const seatId = sub.metadata?.seatId;
    if (!orgId || !seatId) {
      sub.metadata = { ...sub.metadata, status: 'revoked' };
      continue;
    }

    try {
      const orgSnap = await getDocument('organizations', orgId);
      if (!orgSnap.exists()) {
        sub.metadata = { ...sub.metadata, status: 'revoked' };
        continue;
      }

      const orgData = orgSnap.data()!;
      if (orgData.status !== 'active') {
        sub.metadata = { ...sub.metadata, status: 'revoked' };
        continue;
      }

      // Check nextRenewalDate if present
      const nextRenewalDate = orgData.billing?.nextRenewalDate;
      if (nextRenewalDate) {
        const renewalTime = nextRenewalDate.toDate
          ? nextRenewalDate.toDate().getTime()
          : new Date(nextRenewalDate).getTime();
        if (renewalTime < Date.now()) {
          sub.metadata = { ...sub.metadata, status: 'revoked' };
          continue;
        }
      }

      // Check seat assignments
      const seats = orgData.seats || [];
      const seat = seats.find((s: any) => s.seatId === seatId);
      if (!seat || seat.assignedUid !== user.uid) {
        sub.metadata = { ...sub.metadata, status: 'revoked' };
        continue;
      }
    } catch (e) {
      console.error('Failed to validate organization seat:', e);
      // In case of error (e.g., offline or network error), we keep it as-is
    }
  }
}

const SUBSCRIPTION_SYNC_KEY = 'subscriptionSyncedFor';

/**
 * A user without a valid subscription may only be missing a Mollie webhook.
 * Ask the server to pull their subscriptions from Mollie (once per session,
 * since the lookup is slow) and return the reloaded profile.
 */
async function recoverMissedSubscription(uid: string, user: User): Promise<User> {
  if (user.hasValidSubscription()) return user;
  try {
    if (sessionStorage.getItem(SUBSCRIPTION_SYNC_KEY) === uid) return user;
    sessionStorage.setItem(SUBSCRIPTION_SYNC_KEY, uid);
  } catch {
    // storage unavailable: sync anyway
  }
  try {
    await SubscriptionApiService.syncSubscriptions();
    const snap = await getDocument('users', uid);
    return User.fromJson(uid, snap.exists() ? snap.data() : {});
  } catch (e) {
    console.warn('Subscription sync failed:', e);
    return user;
  }
}

export function createAuthStore() {
  onAuthChange(async (fbUser) => {
    if (fbUser) {
      try {
        const originalUid = (fbUser as any).originalUid || fbUser.uid;

        // Initialize API service with current token
        try {
          const idToken = await fbUser.getIdToken();
          BaseApiService.initialize(idToken);
        } catch (tokenErr) {
          console.error('Failed to get ID token:', tokenErr);
        }

        // Fetch real authenticated user profile
        const realUserSnap = await getDocument('users', originalUid);
        let realUser = User.fromJson(originalUid, realUserSnap.exists() ? realUserSnap.data() : {});
        realUser = await recoverMissedSubscription(originalUid, realUser);

        // Validate organization seats
        await validateOrganizationSeat(realUser);

        // Handle target redirect if present
        const accountService = AccountService.getInstance();
        if (realUser.hasValidSubscription() && await accountService.maybeForwardToTarget(originalUid)) {
          return;
        }

        // Restore locale from user profile, fall back to browser language
        const savedLocale = realUser.lang;
        setLocale(savedLocale && SUPPORTED_LOCALES.includes(savedLocale) ? savedLocale : detectBrowserLocale());

        // Fetch claims to check for impersonation
        const idTokenResult = await fbUser.getIdTokenResult();
        const impersonatedUid = idTokenResult.claims.impersonatedUid as string | undefined;

        if (realUser.hasFeature('ADMIN') && impersonatedUid) {
          const impSnap = await getDocument('users', impersonatedUid);
          if (impSnap.exists()) {
            _user = User.fromJson(impersonatedUid, impSnap.data());
            _adminUser = realUser;
          } else {
            console.warn(`Impersonated user ${impersonatedUid} not found, falling back to admin.`);
            _user = realUser;
            _adminUser = null;
            if (typeof window !== 'undefined') {
              sessionStorage.removeItem('impersonatedUid');
            }
          }
        } else {
          _user = realUser;
          _adminUser = null;
          if (typeof window !== 'undefined') {
            sessionStorage.removeItem('impersonatedUid');
          }
        }

        _state = AuthState.AUTHENTICATED;
      } catch (e: any) {
        console.error('Failed to fetch user document:', e);
        _error = e.message || 'Failed to load user data';
        _state = AuthState.ERROR;
      }
    } else {
      _user = null;
      _adminUser = null;
      _state = AuthState.UNAUTHENTICATED;
    }
    _isInitialized = true;
  });

  onOnlineChange((online) => { _isOnline = online; });

  async function impersonateUser(uid: string) {
    const activeUser = _adminUser || _user;
    if (!activeUser || !activeUser.hasFeature('ADMIN')) {
      console.error('Only admins can impersonate users');
      return;
    }
    try {
      _state = AuthState.LOADING;
      const { customToken } = await AdminApiService.impersonate(uid);
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('impersonatedUid', uid);
      }
      await signInWithCustomToken(customToken);
      if (typeof window !== 'undefined') {
        window.location.href = '/';
      }
    } catch (err: any) {
      console.error('Failed to impersonate:', err);
      _error = err.message || 'Failed to impersonate user';
      _state = AuthState.ERROR;
    }
  }

  async function stopImpersonating() {
    try {
      _state = AuthState.LOADING;
      const { customToken } = await AdminApiService.impersonate(null);
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('impersonatedUid');
      }
      await signInWithCustomToken(customToken);
      if (typeof window !== 'undefined') {
        window.location.href = '/admin';
      }
    } catch (err: any) {
      console.error('Failed to stop impersonation:', err);
      _error = err.message || 'Failed to stop impersonation';
      _state = AuthState.ERROR;
    }
  }

  async function refreshUser() {
    if (_user) {
      const originalUid = _adminUser ? _adminUser.uid : _user.uid;
      const realUserSnap = await getDocument('users', originalUid);
      if (realUserSnap.exists()) {
        const realUser = User.fromJson(originalUid, realUserSnap.data());
        await validateOrganizationSeat(realUser);
        if (_adminUser) {
          const impersonatedUid = _user.uid;
          const impSnap = await getDocument('users', impersonatedUid);
          if (impSnap.exists()) {
            const impUser = User.fromJson(impersonatedUid, impSnap.data());
            await validateOrganizationSeat(impUser);
            _user = impUser;
          }
          _adminUser = realUser;
        } else {
          _user = realUser;
        }
      }
    }
  }

  return {
    get state() { return _state; },
    get user() { return _user; },
    get adminUser() { return _adminUser; },
    get isImpersonating() { return !!_adminUser; },
    get error() { return _error; },
    get isInitialized() { return _isInitialized; },
    get isOnline() { return _isOnline; },
    setError(e: string | null) { _error = e; },
    impersonateUser,
    stopImpersonating,
    refreshUser
  };
}
